import { NumberFormatOptions, StepperDirection } from '../number-field.types';

import { NumberFormatter } from './number-formatter';

export type StepperProps = NumberFormatOptions & {
  input: HTMLInputElement;
};

/**
 * Stepper is a utility class that handles incrementing and decrementing
 * of numeric input values with respect to formatting rules, `min`, `max`, and `step` attributes.
 */
export class Stepper {
  private _input: HTMLInputElement;
  private _formatter: NumberFormatter;

  /**
   * Constructs a new Stepper instance.
   *
   * @param input - The HTML input element that should be stepped.
   * @param props - Number formatting options (locale, format style, etc.).
   */
  constructor({ input, ...props }: StepperProps) {
    this._input = input;
    this._formatter = new NumberFormatter(props);
  }

  /**
   * Updates the input field's value by incrementing or decrementing based on direction,
   * while respecting the `min`, `max`, and `step` attributes.
   *
   * @param direction - The direction of step. `'up'` to increment, `'down'` to decrement.
   */
  public updateInputValue(direction: StepperDirection) {
    const nextValue = this.calculateNextValue(direction);
    return this.applyNextValue(nextValue);
  }

  /**
   * Applies the given numeric value to the input element in formatted form
   * and triggers a native input event to ensure form syncing.
   *
   * @param value - The numeric value to apply to the input.
   */
  public applyNextValue(value: number | string) {
    const { formatted } = this._formatter.format(value);

    const setter = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      'value'
    )?.set;

    if (setter) {
      setter.call(this._input, formatted);
      this._input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    return { raw: this._input.value, formatted };
  }

  /**
   * Calculates the next value for the input based on the current value,
   * direction, step size, and min/max boundaries.
   *
   * @param direction - The direction to step: `'up'` or `'down'`.
   * @returns The next numeric value after applying step and clamping.
   */
  private calculateNextValue(direction: StepperDirection): number {
    const step = parseFloat(this._input.step) || 1;
    const min = this._input.min !== '' ? parseFloat(this._input.min) : -Infinity;
    const max = this._input.max !== '' ? parseFloat(this._input.max) : Infinity;
    const current = this._formatter.unformat(this._input.value) ?? 0;

    const delta = direction === StepperDirection.Up ? step : -step;
    const next = current + delta;

    // Clamp to min and max
    return Math.min(Math.max(next, min), max);
  }
}
